import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {ComposerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {StandaloneComposerStatusEnum} from '../../../helpers/composerHelper';
import {ComposerContainer} from './composerContainer';
import {StandaloneComposerMessage} from './standaloneComposerMessage';

interface StandaloneComposerProps {
  message?: DraftMessageModel | undefined;
  status: StandaloneComposerStatusEnum;
  panelId: ComposerPanelId;
}

interface ContainerProps {
  $isValidDraft: boolean;
}

const StyledContainerDiv = styled.div<ContainerProps>`
  display: flex;
  height: 100%;
  ${(p) => !p.$isValidDraft && center()};
`;

export const StandaloneComposer: FC<StandaloneComposerProps> = (props) => {
  const {message, panelId, status} = props;

  return (
    <StyledContainerDiv $isValidDraft={status === StandaloneComposerStatusEnum.VALID_DRAFT}>
      {maybeRenderMessageFromStatus(status)}
      {message && (
        <ComposerContainer
          message={message}
          panelId={panelId}
          conversation={null}
          canPopout={false}
          isOnlyComposer
          shouldFocus
        />
      )}
    </StyledContainerDiv>
  );
};

/*
 * Helpers.
 */

const maybeRenderMessageFromStatus = (status: StandaloneComposerStatusEnum) => {
  switch (status) {
    case StandaloneComposerStatusEnum.DELETED:
      return (
        <StandaloneComposerMessage
          title={<Trans id="standalone-composer-state-draft-deleted-title">Draft has been deleted</Trans>}
          description={
            <Trans id="standalone-composer-state-draft-deleted-description">
              If you would like to compose a new draft, please refresh.
            </Trans>
          }
        />
      );
    case StandaloneComposerStatusEnum.SENDING:
      return (
        <StandaloneComposerMessage
          title={<Trans id="standalone-composer-state-draft-sending-title">Message is sending</Trans>}
          description={
            <Trans id="standalone-composer-state-draft-sending-description">
              This should only take a few seconds
            </Trans>
          }
        />
      );
    case StandaloneComposerStatusEnum.SENT:
      return (
        <StandaloneComposerMessage
          title={<Trans id="standalone-composer-state-draft-sent-title">Message was sent!</Trans>}
          description={
            <Trans id="standalone-composer-state-draft-sent-description">You can close this window.</Trans>
          }
        />
      );
    case StandaloneComposerStatusEnum.NOT_AUTHOR_SHARED:
      return (
        <StandaloneComposerMessage
          title={
            <Trans id="standalone-composer-state-draft-not-author-title">
              You are no longer the author of this draft.
            </Trans>
          }
          description={
            <Trans id="standalone-composer-state-draft-not-author-description">
              A teammate has taken over this draft. You can close this window.
            </Trans>
          }
        />
      );
    case StandaloneComposerStatusEnum.INVALID_DRAFT:
      return (
        <StandaloneComposerMessage
          title={
            <Trans id="standalone-composer-state-something-went-wrong-title">Something went wrong.</Trans>
          }
          description={
            <Trans id="standalone-composer-state-something-went-wrong-description">
              Please refresh the window.
            </Trans>
          }
        />
      );
    default:
      return null;
  }
};
